import { EMPTY_VALUE } from './formatters';

/**
 * Human labels for the closed enums the API returns as keys (DESIGN_SYSTEM.md
 * §4 "Names, not keys"). A raw key is a machine value: sort, filter and send
 * the key; show the label.
 *
 * Each `*_KEYS` list mirrors a pgEnum in api/src/db/schema.ts, and each label
 * map is a `Record` over that union, so a new key fails the typecheck until it
 * has a label (and labels.test.ts checks the same at runtime). Coverage reasons
 * are NOT here: lib/subscriptionErrors.ts owns that copy, and the codes are
 * never shown to tenants.
 */
export const SENSOR_TYPE_KEYS = ['camera', 'lidar', 'radar', 'rf_sensor', 'fused'] as const;
export type SensorType = (typeof SENSOR_TYPE_KEYS)[number];

export const TAG_TYPE_KEYS = ['object', 'activity'] as const;
export type TagType = (typeof TAG_TYPE_KEYS)[number];

export const NODE_TYPE_KEYS = ['managed', 'edge'] as const;
export type NodeType = (typeof NODE_TYPE_KEYS)[number];

export const SENSOR_TYPE_LABELS: Record<SensorType, string> = {
  camera: 'Camera',
  lidar: 'LiDAR',
  radar: 'Radar',
  rf_sensor: 'RF sensor',
  fused: 'Fused',
};

export const TAG_TYPE_LABELS: Record<TagType, string> = {
  object: 'Object',
  activity: 'Activity',
};

/** Every processor runs in the cloud today; `managed` IS cloud. */
export const NODE_TYPE_LABELS: Record<NodeType, string> = {
  managed: 'Cloud (managed)',
  edge: 'Edge',
};

/** "rf_sensor" → "Rf sensor". Only for a key no map knows yet. */
export function humanizeKey(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim();
  if (!words) return EMPTY_VALUE;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The map's label, then a humanized key, then EMPTY_VALUE for nothing. */
export function formatEnum(map: Readonly<Record<string, string>>, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value] : humanizeKey(value);
}

/** "camera, rf_sensor" → "Camera, RF sensor"; EMPTY_VALUE for none. */
export function formatSensorTypes(types: readonly string[] | null | undefined): string {
  if (!types || types.length === 0) return EMPTY_VALUE;
  return types.map((t) => formatEnum(SENSOR_TYPE_LABELS, t)).join(', ');
}
